// components/playground/Table/LineItemRow.jsx
import React, { useState } from 'react';
import { EyeIcon, EyeSlashIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import EditableCell from './EditableCell';

const LineItemRow = ({
  item,
  indent = 0,
  viewMode,
  months,
  quarters,
  editingCell,
  userAdjustments,
  selectedYear,
  currentYear,
  currentMonth,
  isVisibilityEditMode,
  isHidden,
  showHiddenItems,
  highlightedRow,
  onCellEdit,
  onCellSave,
  onCellCancel,
  onCellChange,
  onQuarterlyCellEdit,
  onQuarterlyCellSave,
  onToggleItemVisibility,
  onLineItemClick,
  formatCurrency,
  setRowRef,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const indentClass = indent === 0 ? 'pl-8' : indent === 1 ? 'pl-12' : 'pl-16';
  const isHighlighted = highlightedRow === item.account_id;
  const isClickable = String(item.account_id) === '154' || 
                    String(item.account_id) === '100' ||  // ✅ ADD Wages and Salary
                    item.fully_qualified_name?.includes('Marketing Technology Stack')||
                    item.fully_qualified_name?.includes('Wages & Salaries');

  const fqn = item.fully_qualified_name || item.account_name;
  const parts = fqn.split(':');
  const displayName = parts.length > 1 ? parts[parts.length - 1] : item.account_name;

  const isEdited = (monthNum) => {
    const key = `${selectedYear}-${item.account_id}-${monthNum}`;
    const hasAdjustment = userAdjustments[key] !== undefined;
    
    if (!hasAdjustment) return false;
    
    // ✅ FIX: Find the month object to check if it's projected
    const monthData = months.find(m => m.month_num === monthNum);
    
    if (monthData) {
      // Show as edited if month is projected (not actual/historical)
      return monthData.is_projected;
    }
    
    // ✅ Fallback logic
    if (selectedYear < currentYear) {
      // Historical year - no edits allowed
      return false;
    }
    
    if (selectedYear === currentYear) {
      // Current year - check if month is in future
      if (!currentMonth) return false;
      return Number(monthNum) > Number(currentMonth);
    }
    
    // Future years - all months can be edited
    return true;
  };

  // Handle row click - prevent if clicking on editable cells
  const handleRowClick = (e) => {
    if (!isClickable) return;
    
    // Don't trigger if clicking on visibility button or editable cells
    if (e.target.closest('button') || e.target.closest('input')) {
      return;
    }
    
    onLineItemClick(item.account_id);
  };

  return (
    <tr 
      ref={(el) => setRowRef(item.account_id, el)}
      onClick={handleRowClick}
      onMouseEnter={() => isClickable && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`transition-all duration-200 ${
        isHighlighted 
          ? 'bg-yellow-200 ring-4 ring-yellow-400 shadow-lg animate-pulse' 
          : showHiddenItems && isHidden
          ? 'bg-red-50 opacity-60'
          : isClickable && isHovered
          ? 'bg-blue-50 shadow-sm cursor-pointer'
          : isClickable
          ? 'hover:bg-blue-25 cursor-pointer'
          : 'hover:bg-gray-50'
      }`}
      title={isClickable ? 'Click to view transaction details' : undefined}
    >
      <td 
        className={`${indentClass} py-1.5 text-xs text-gray-700 border-b border-gray-100 ${
          isHighlighted ? 'font-bold' : ''
        }`}
      >
        <div className="flex items-center gap-2">
          {isVisibilityEditMode && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleItemVisibility(item.account_id);
              }}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              title={isHidden ? 'Show this item' : 'Hide this item'}
            >
              {isHidden ? (
                <EyeSlashIcon className="w-4 h-4" />
              ) : (
                <EyeIcon className="w-4 h-4" />
              )}
            </button>
          )}
          
          <span className={showHiddenItems && isHidden ? 'line-through text-gray-400' : ''}>
            {displayName}
          </span>

          {/* Info icon for clickable items */}
          {isClickable && (
            <InformationCircleIcon 
              className={`w-4 h-4 transition-colors ${
                isHovered 
                  ? 'text-blue-600' 
                  : 'text-gray-400'
              }`}
            />
          )}
        </div>
      </td>
      
      {viewMode === 'monthly' ? (
        months.map((month) => {
          const amount = item.projected_amounts[month.month_num] || 0;
          const isEditingThisCell =
            editingCell?.accountId === item.account_id &&
            editingCell?.monthNum === month.month_num &&
            !editingCell?.isQuarterly;
          const edited = isEdited(month.month_num);

          return (
            <td
              key={month.month_num}
              className={`px-2 py-1.5 text-center text-xs border-b border-gray-100 ${
                month.is_actual
                  ? 'bg-white text-gray-700'
                  : edited
                  ? 'bg-amber-100 ring-1 ring-amber-400'
                  : 'bg-violet-200'
              } ${!month.is_actual ? 'cursor-pointer hover:bg-blue-100' : ''}`}
              onClick={(e) => {
                if (!month.is_actual) {
                  e.stopPropagation();
                  onCellEdit(item.account_id, month.month_num, amount);
                }
              }}
            >
              <EditableCell
                value={isEditingThisCell ? editingCell.value : amount}
                isEditing={isEditingThisCell}
                isActual={month.is_actual}
                isEdited={edited}
                onEdit={() => !month.is_actual && onCellEdit(item.account_id, month.month_num, amount)}
                onSave={onCellSave}
                onCancel={onCellCancel}
                onChange={onCellChange}
                formatValue={formatCurrency}
              />
            </td>
          );
        })
      ) : (
        quarters.map((quarter) => {
          const quarterTotal = quarter.months.reduce((sum, month) => {
            return sum + (item.projected_amounts[month.month_num] || 0);
          }, 0);
          
          const isEditingThisCell =
            editingCell?.accountId === item.account_id &&
            editingCell?.isQuarterly &&
            JSON.stringify(editingCell?.quarterMonths) === JSON.stringify(quarter.months.map(m => m.month_num));
          
          const hasAnyEdited = quarter.months.some(month => isEdited(month.month_num));

          return (
            <td
              key={quarter.quarter}
              className={`px-2 py-1.5 text-center text-xs border-b border-gray-100 ${
                quarter.is_actual
                  ? 'bg-white text-gray-700'
                  : hasAnyEdited
                  ? 'bg-amber-100 ring-1 ring-amber-400'
                  : quarter.is_partial
                  ? 'bg-green-50'
                  : 'bg-violet-200'
              }`}
            >
              <EditableCell
                value={isEditingThisCell ? editingCell.value : quarterTotal}
                isEditing={isEditingThisCell}
                isActual={quarter.is_actual}
                isEdited={hasAnyEdited}
                onEdit={() => !quarter.is_actual && !isClickable && onQuarterlyCellEdit(item.account_id, quarter.months, quarterTotal)}
                onSave={onQuarterlyCellSave}
                onCancel={onCellCancel}
                onChange={onCellChange}
                formatValue={formatCurrency}
              />
            </td>
          );
        })
      )}
      
      <td className="px-4 py-3 text-center text-xs bg-gray-50 border-b border-gray-100">
        {formatCurrency(
          Object.values(item.projected_amounts).reduce((s, v) => s + v, 0)
        )}
      </td>
    </tr>
  );
};

export default LineItemRow;